/**
 * Sprint UX-BR — Dicionário PT-BR (Brasil).
 *
 * IMPORTANTE — TERMOS QUE NÃO SÃO TRADUZIDOS:
 *   device_id, customer_id, hostname, SSID, OTA, Wi-Fi, IP, Gateway,
 *   DNS, DHCP, relay_reboot, watchdog, firmware, heartbeat, JSON, API,
 *   HTTP, BGP, IXC, GIS, NOC, payload, log, tag, snapshot
 *
 * Esses aparecem no meio de frases como nomes próprios:
 *   "Versão Firmware"          (firmware preservado)
 *   "Senha Wi-Fi muda"         (Wi-Fi preservado)
 *   "Último Heartbeat"         (Heartbeat preservado)
 *   "Configurar OTA"           (OTA preservado)
 *   "Reiniciar Relé"           (Relé traduzido — não é termo técnico)
 */

export const ptBR = {
  // -------------------------------------------------------------------
  // Comuns — botões, ações, estados básicos
  // -------------------------------------------------------------------
  common: {
    apply: "Aplicar",
    cancel: "Cancelar",
    close: "Fechar",
    save: "Salvar",
    delete: "Excluir",
    edit: "Editar",
    create: "Criar",
    add: "Adicionar",
    remove: "Remover",
    confirm: "Confirmar",
    retry: "Tentar novamente",
    reload: "Recarregar",
    refresh: "Atualizar",
    loading: "carregando...",
    busy: "...",
    error: "Erro",
    success: "Sucesso",
    yes: "Sim",
    no: "Não",
    none: "Nenhum",
    search: "Buscar",
    filter: "Filtrar",
    clear: "Limpar",
    total: "Total",
    of: "de",
    showing: "Mostrando",
    showingOf: "Mostrando {n} de {total}",
    select: "Selecionar",
    selected: "selecionado(s)",
    unselect: "Desmarcar",
    selectAll: "Selecionar tudo",
    open: "Abrir",
    download: "Baixar",
    upload: "Enviar",
    copy: "Copiar",
    copied: "Copiado!",
    settings: "Configurações",
    advanced: "Avançado",
    optional: "opcional",
    required: "obrigatório",
    dismiss: "Dispensar",
    back: "Voltar",
    next: "Próximo",
    previous: "Anterior",
  },

  // -------------------------------------------------------------------
  // Status (sync, online, etc) — manter siglas técnicas em maiúsculas
  // -------------------------------------------------------------------
  status: {
    online: "Online",
    offline: "Offline",
    pending: "Pendente",
    synced: "Sincronizado",
    outdated: "Desatualizado",
    unknown: "Desconhecido",
    ok: "OK",
    fail: "Falhou",
    busy: "Ocupado",
    skipped: "Pulado",
    applied: "Aplicado",
    inProgress: "Em progresso",
    completed: "Concluído",
    cancelled: "Cancelado",
    expired: "Expirado",
    acked: "Confirmado",      // ACK
    delivered: "Entregue",
    failed: "Falhou",
    weakSignal: "Sinal fraco",
  },

  // -------------------------------------------------------------------
  // Navegação principal
  // -------------------------------------------------------------------
  nav: {
    dashboard: "Painel",
    fleet: "Frota",
    fleetMap: "Mapa da Frota",
    devices: "Dispositivos",
    firmware: "Firmware",      // termo técnico preservado
    rollouts: "Implantações",
    ota: "OTA / Firmware",
    production: "Produção",
    templates: "Templates",     // termo de configuração — preservado
    provisioning: "Provisionamento",
    installations: "Instalações",
    inventory: "Estoque",
    health: "Saúde",
    teams: "Equipes",
    rules: "Regras",
    audit: "Auditoria",
    observability: "Observabilidade",
    alerts: "Alertas",
    automation: "Automação",
    playbooks: "Playbooks",
    fieldOps: "Field Ops",
    accounts: "Contas de login",
    companies: "Empresas",
    settings: "Configurações",
    tenants: "Empresas",
    tenantApproval: "Aprovações",
    addDevice: "Adicionar dispositivo",
    tenantUsers: "Usuários da empresa",
    history: "Histórico",
  },

  // -------------------------------------------------------------------
  // NOC v3 — grupos da sidebar (UI Rework Global)
  // -------------------------------------------------------------------
  navgroup: {
    overview: "Visão Geral",
    operations: "Operações",
    firmware: "Firmware",
    monitoring: "Monitoramento",
    automation: "Automação",
    admin: "Administração",
    portal: "Portal do Cliente",
    // chaves antigas mantidas p/ compatibilidade
    dashboard: "Painel",
    operation: "Operação",
    field: "Campo",
    config: "Configuração",
    platform: "Plataforma",
  },
  shell: {
    collapse: "Recolher menu",
    expand: "Expandir menu",
    menu: "Menu",
    favorites: "Favoritos",
    toggleFavorite: "Fixar nos favoritos",
    refresh: "Atualizar agora",
    searchPlaceholder: "Buscar telas, painéis…",
    searchEmpty: "Nenhum resultado.",
    kbdNavigate: "navegar",
    kbdSelect: "abrir",
  },
  quick: {
    newDevice: "Provisionar dispositivo",
    alerts: "Ver alertas",
    map: "Mapa da frota",
    automation: "Automação",
  },

  // -------------------------------------------------------------------
  // Dispositivo (geral) — preservar device_id, IP, MAC, SSID, etc.
  // -------------------------------------------------------------------
  device: {
    title: "Dispositivo",
    devices: "Dispositivos",
    deviceCount: "{n} dispositivos",
    fwVersion: "Versão Firmware",   // Firmware preservado
    lastHeartbeat: "Último Heartbeat", // Heartbeat preservado
    lastSeen: "Visto por último",
    relay: "Relé",
    relayOn: "Relé ON",
    relayOff: "Relé OFF",
    relayReboot: "Reiniciar Relé",
    relayNoState: "Relé sem estado conhecido",
    espReboot: "Reiniciar ESP32",
    onlineOnly: "Somente Online",
    offlineOnly: "Somente Offline",
    relayState: "Estado do Relé",
    minFwVersion: "Versão mínima Firmware",
    rssi: "RSSI",
    rssiThreshold: "Limiar RSSI",
    customer: "Cliente",
    group: "Grupo",
    region: "Região",
    tags: "Tags",                   // tag como termo técnico
    location: "Localização",
    address: "Endereço",
    coords: "Coordenadas",
    configRevision: "Versão Config",
    configState: "Estado config",
    lastConfigSync: "Último sync",
    lastSchedule: "Última agenda",
    open: "Abrir",
    openDetails: "Abrir detalhes",
    move: "Mover",
    configure: "Configurar",
    history: "Histórico",
    addToBatch: "Selecionar + lote",
    selectInBatch: "Selecionar em lote",
    notFound: "Dispositivo não encontrado",
    noSnapshot: "sem snapshot",
    pendingConfig: "comandos config aguardando ACK",
    firmwareOldWarning: "device ainda não enviou snapshot (firmware ≥ 2.6.1 necessário)",
  },

  // -------------------------------------------------------------------
  // Abas (DeviceConfigModal etc)
  // -------------------------------------------------------------------
  tabs: {
    wifi: "Rede",
    management: "Gerência",
    watchdog: "Supervisão",         // Supervisão (Watchdog)
    schedule: "Agenda",
    advanced: "Avançado",
  },

  // -------------------------------------------------------------------
  // Templates de configuração
  // -------------------------------------------------------------------
  template: {
    title: "Template",
    templates: "Templates",         // tag interna
    apply: "Aplicar Template",
    applyAll: "🚀 Aplicar Todos",
    applyFiltered: "🎯 Aplicar Filtrados",
    applySelected: "✓ Aplicar Selecionados ({n})",
    previewDiff: "🔬 Pré-visualizar Alterações",
    previewDryRun: "🔍 Pré-visualizar (dry-run)",
    cancelRun: "Cancelar Run",
    cancelRunConfirm: "Cancelar esta execução? Comandos pendentes não serão mais enviados.",
    target: "Alvo",
    selector: "Seletor",
    risk: "Risco",
    riskSummary: "Resumo de Riscos",
    riskyChanges: "Mudanças sensíveis",
    risks: "riscos",
    risksCount: "{n} risco(s)",
    confirmRisky: "Confirmo — entendo os riscos",
    confirmRiskyRequired:
      "Esta ação pode desconectar dispositivos. Marque \"Confirmo — entendo os riscos\" e tente novamente.",
    deviceCount: "Dispositivos",
    selectedDevices: "Dispositivos selecionados",
    withChanges: "com mudanças",
    noChanges: "Sem alterações",
    withoutSnapshot: "Sem Snapshot",
    alreadyIdentical: "Já idênticos",
    onlyChanged: "Somente devices com mudanças",
    onlyRisky: "Somente devices com risco",
    excludeWithoutSnapshot: "Excluir sem snapshot",
    excludeIdentical: "Excluir já idênticos",
    schedule: "Agendar",
    scheduledAt: "Agendado para",
    scheduleTime: "Janela de envio",
    runHistory: "Histórico de Execuções",
    runIdShort: "Run #{id}",
    selectorLabel: {
      ids: "device_id explícito",
      group: "Grupo",
      customer: "Cliente",
      region: "Região",
      tags: "Tags",
    },
    stagger: "Espaçamento entre dispositivos (segundos)",
    commandStagger: "Espaçamento entre comandos (segundos)",
    force: "Forçar (ignorar incompatibilidade de firmware)",
    selectorEmpty: "Selecione ao menos 1 dispositivo / cliente / grupo / região / tag.",
    filterApplyBlock: "🎯 Filtros para Aplicar",
    filterViewBlock: "Visualizar",
    onlyRisks: "só riscos",
    onlyWithChanges: "só com mudanças",
    onlyWithoutSnapshot: "só sem snapshot",
    selectedManually: "{n} selecionado(s) manualmente",
    clearSelection: "limpar seleção",
    noDeviceMatches: "Nenhum dispositivo casa com os filtros atuais.",
  },

  // -------------------------------------------------------------------
  // Sprint UX-BR-3 — Listagem/grid de Templates (ConfigTemplatesPage)
  // -------------------------------------------------------------------
  templatesPage: {
    title: "📋 Templates de Configuração",
    subtitle:
      "Templates reutilizáveis aplicáveis em massa em dispositivos, " +
      "grupos, clientes ou regiões.",
    allScopes: "Todos os escopos",
    generalHistory: "📜 Histórico geral",
    newTemplate: "+ Novo Template",
    empty: "Nenhum template ainda.",
    emptyAction: "Criar primeiro template",
    errLoad: "Erro ao carregar templates",
    errDelete: "Erro ao excluir: ",
    confirmDelete:
      'Excluir template "{name}"?\n\nO histórico de execuções também será removido.',
    colName: "Nome",
    colScope: "Escopo",
    colTypes: "Tipos",
    colUpdated: "Atualizado",
    colStatus: "Status",
    colActions: "Ações",
    enabledOn: "● HABILITADO",
    enabledOff: "○ DESABILITADO",
    btnApply: "🚀 Aplicar",
    titleApply: "Aplicar",
    titleHistory: "Histórico",
    titleEdit: "Editar",
    titleDuplicate: "Duplicar",
    titleDelete: "Excluir",
    duplicateSuffix: "(cópia)",
    visibilityGlobal: "Global",
    visibilityPrivate: "Privado",
    visibilityMixed: "Misto",
  },

  // -------------------------------------------------------------------
  // Sprint UX-BR-3 — Editor de Template (TemplateBuilder)
  // -------------------------------------------------------------------
  templateBuilder: {
    titleEdit: "✏ Editar Template",
    titleNew: "📄 Novo Template",
    name: "Nome",
    namePlaceholder: "Ex: Restaurantes WiFi 5G",
    enabled: "Habilitado",
    descriptionLabel: "Descrição (opcional)",
    descriptionPlaceholder: "Notas sobre quando aplicar este template",
    includeInTemplate: 'Incluir "{section}" no template',
    payloadFinal: "Payload final",
    save: "Salvar",
    create: "Criar",
    saving: "Salvando...",
    cancel: "Cancelar",
    sectionLabel: {
      wifi: "Rede Wi-Fi",
      management: "Gerência",
      watchdog: "Supervisão",
      schedule: "Agenda",
      advanced: "Avançado",
    },
    addEntry: "+ Adicionar entrada",
    removeEntry: "Remover entrada",
    pulseReboot: "Pulso de reinício",
    onOffFixed: "Ligar/Desligar fixo",
    days: "Dias",
    pulseHour: "Hora do pulso",
    pulseDuration: "Duração (s)",
    onHour: "Hora ON",
    offHour: "Hora OFF",
    onlyDays: "Apenas dias",
    exceptions: "Exceções (datas)",
    addException: "Adicionar exceção",
    err: {
      ssidRequired: "SSID obrigatório quando Wi-Fi está incluído.",
      pulseInvalid:
        "Pulso de reinício: precisa de dias + Hora do pulso + Duração 1-300s.",
      onOffInvalid:
        "ON/OFF fixo: precisa de dias + Hora ON + Hora OFF.",
      nameRequired: "Nome obrigatório",
      includeAtLeastOne: "Inclua ao menos uma seção (marque o checkbox)",
      networkSsidRequired: "Rede: SSID é obrigatório.",
      scheduleNoEntries:
        "Agenda habilitada mas sem entradas válidas.\n\n" +
        "ON/OFF fixo: precisa de dias + Hora ON + Hora OFF (não iguais).\n" +
        "Pulso de reinício: precisa de dias + Hora do pulso + Duração 1-300s.",
      scheduleNoEntriesShort:
        'Agenda habilitada mas sem entradas. Clique em "{add}" para criar pelo menos uma.',
      advancedPrefix: "Avançado:\n",
      // Sprint 4.7.1 — validações por entry (ScheduleEditor compartilhado)
      daysRequired: "selecione ao menos um dia da semana.",
      pulseTimeRequired: "modo pulso exige Hora do pulso.",
      pulseDurationRange: "Duração deve estar entre 1 e 300 segundos.",
      pulseActionInvalid:
        "ação inválida no modo pulso — use relay_reboot ou esp_reboot.",
      onTimeRequired: "modo ON/OFF exige Hora ON.",
      offTimeRequired: "modo ON/OFF exige Hora OFF.",
      onOffEqual: "Hora ON e Hora OFF não podem ser iguais.",
      onOffEqualShort: "Hora ON e OFF não podem ser iguais",
      onOffActionInvalid:
        "ação inválida no modo ON/OFF — use relay_on ou relay_off.",
      pulseRequiresFields: "Pulso exige Hora e Duração",
    },
    // Sprint 4.7.1 — extras compartilhados pelo ScheduleEditor
    noDaysSelected: "nenhum dia selecionado",
    scheduleType: "Tipo de agenda",
    pulseHint: "Desliga e religa o relé num horário específico",
    onOffHint: "Liga em hora X, desliga em hora Y",
    pulseExplain: "Pulso = desliga em {time} e religa {duration}s depois",
    exceptionsLabel: "Exceções (datas a ignorar)",
    exceptionsEmpty: "Nenhuma exceção. Use para feriados etc.",
  },

  // -------------------------------------------------------------------
  // Sprint UX-BR-3 — Drawer de Histórico de Execuções (RunHistoryDrawer)
  // -------------------------------------------------------------------
  runHistory: {
    titleTemplate: "📜 Histórico do Template",
    titleGeneral: "📜 Histórico Geral",
    empty: "Nenhuma execução ainda.",
    runId: "Execução {short}",
    dry: "DRY",
    dryRun: "DRY-RUN",
    targets: "Alvos",
    created: "Comandos",
    skipped: "Pulados",
    skippedSuffix: " pulados",
    createdAt: "Criado em",
    createdBy: "Criado por",
    cancelRun: "🛑 Cancelar Execução",
    cancelling: "Cancelando...",
    cancelConfirm:
      "Isso cancelará comandos ainda não executados.\n\n" +
      "• Comandos confirmados (ACK) NÃO serão tocados.\n" +
      "• Comandos pendentes/entregues serão marcados como falhos.\n" +
      "• Comandos futuros (agendados) não serão entregues.\n\n" +
      "Confirma o cancelamento desta execução?",
    cancelTitle: "Execução cancelada",
    cancelAt: "Cancelada em {date}",
    cancelCmds: "comando(s) cancelado(s)",
    cancelAlreadyAcked: "já confirmados (não tocados)",
    cancelTerminal: "em estado terminal (pulados)",
    risks: "Riscos",
    errCancel: "Erro ao cancelar execução",
    status: {
      completed: "Concluído",
      partial: "Parcial",
      failed: "Falhou",
      dry_run: "Pré-visualização",
      canceled: "Cancelado",
      pending: "Pendente",
    },
  },

  // -------------------------------------------------------------------
  // Sprint UX-BR-3 — Lista de Dispositivos (DevicesList)
  // -------------------------------------------------------------------
  devicesList: {
    searchPlaceholder: "Buscar: device_id, IP, firmware, código de cliente…",
    all: "Todos",
    online: "Online",
    degraded: "Degradado",
    offline: "Offline",
    noMatch: "Nenhum dispositivo corresponde aos filtros atuais.",
    empty: "Nenhum dispositivo registrado ainda.",
    colStatus: "Status",
    colDeviceId: "Identificação",
    colCustomer: "Cliente",
    colFw: "Firmware",
    colIp: "IP",
    colRelay: "Relé",
    colRssi: "RSSI",
    colUptime: "Tempo ativo",
    colHeap: "Heap livre",
    colRebootReason: "Último reboot",
    colLastSeen: "Visto por último",
    btnDetails: "detalhes",
    // Sprint 5.0.3 — Badge IXC ao lado do device_id
    ixcBindingTooltip: "Dispositivo tem vínculo IXC ativo",
  },

  // -------------------------------------------------------------------
  // Sprint UX-BR-3 — Detalhe de um Dispositivo (DeviceDetail)
  // -------------------------------------------------------------------
  deviceDetail: {
    back: "← voltar",
    relayRebootTitle:
      "Desliga o relé temporariamente e religa em seguida",
    espRebootTitle:
      "Reinicia o dispositivo ESP32 (ficará offline ~30s)",
    relayReboot: "Reiniciar Relé",
    espReboot: "Reiniciar ESP32",
    failedLoad: "Falha ao carregar dispositivo: ",
    summary: "Resumo",
    nvsVersions: "Versões NVS",
    lastPayload: "Último payload",
    payloadEmpty:
      "Nenhum payload registrado ainda. Aguarde o primeiro heartbeat.",
    deviceId: "device_id",
    uuid: "UUID",
    customer: "Cliente",
    firmware: "Firmware",
    ipLocal: "IP local",
    relay: "Relé",
    uptime: "Tempo ativo",
    lastBootReason: "Última razão de boot",
    createdAt: "Criado em",
    lastSeen: "Visto por último",
    // S7.5C-B — Fleet Health card (lê data.heartbeat.* da S7.5C-A)
    health: {
      title: "Saúde do Equipamento",
      bootCount: "Boots",
      rebootReason: "Último reboot",
      heapFree: "Heap livre",
      heapMin: "Heap mínimo",
      rssiCurrent: "RSSI atual",
      rssiMin: "RSSI mínimo",
      wifiUptime: "Wi-Fi uptime",
      wifiDisconnects: "Desconexões Wi-Fi",
      lastOtaVersion: "Última OTA",
      lastOtaConfirm: "OTA confirmada",
      otaEventsTitle: "Eventos OTA recentes",
      otaEventsEmpty: "Sem eventos OTA registrados.",
      otaEventsLoading: "Carregando eventos OTA…",
      otaEventsError: "Falha ao carregar eventos OTA: {msg}",
    },
    // S7.6-A — Device Inventory Card no topo do DeviceDetail.
    inventory: {
      title: "Inventário do Equipamento",
      serial: "Serial / QR",
      mac: "MAC",
      customer: "Cliente",
      installedAt: "Instalado em",
      lastHb: "Último HB",
      firmware: "Firmware",
    },
  },

  // -------------------------------------------------------------------
  // Sprint UX-BR-3 — Releases de Firmware (FirmwareReleases)
  // -------------------------------------------------------------------
  firmwareReleases: {
    title: "Versões de Firmware",
    subtitle:
      "Gestão de versões para OTA individual por dispositivo. " +
      "Faça upload aqui e dispare OTA no detalhe de cada dispositivo.",
    newRelease: "Nova Versão",
    version: "Versão",
    versionPlaceholder: "2.4.0",
    binFile: "Arquivo .bin",
    notes: "Notas (opcional)",
    notesPlaceholder: "Sprint 3.5 — OTA Fase 1",
    upload: "Enviar",
    uploading: "Enviando...",
    releases: "Versões",
    releasesCount: "Versões ({n})",
    showDeprecated: "Mostrar obsoletos",
    empty: "Nenhuma versão ainda. Use o formulário acima para enviar um .bin.",
    colVersion: "Versão",
    colStatus: "Status",
    colSha: "SHA256",
    colSize: "Tamanho",
    colCreated: "Criado em",
    colNotes: "Notas",
    statusDeprecated: "OBSOLETO",
    statusActive: "ATIVA",
    btnRollout: "⇪ implantação",
    btnRolloutTitle: "Implantação em massa",
    btnUndeprecate: "↩ reativar",
    btnUndeprecateTitle: "Reativar versão",
    btnDeprecate: "ⓧ obsoletar",
    btnDeprecateTitle: "Marcar como obsoleto (não apaga arquivo)",
    btnDelete: "🗑",
    btnDeleteTitle: "Apagar arquivo do disco (irreversível)",
    confirmDelete:
      'Apagar versão "{version}" definitivamente?\n\n' +
      "Esta ação NÃO pode ser desfeita.",
    confirmDeprecate:
      'Marcar versão "{version}" como obsoleta?\n\n' +
      "Dispositivos que estão usando essa versão não são afetados, " +
      "mas ela não será oferecida para novas OTAs.",
    errUpload: "Erro ao enviar",
    errRollout: "Erro ao disparar implantação",
    rolloutProgress: "Progresso",
    rolloutFailures: "Falhas",
    rolloutDone: "Concluído",
    cancelRollout: "Cancelar implantação",

    // Erros / prompts (longos)
    errLoad: "Erro ao carregar versões",
    errDelete: "Erro ao apagar versão",
    errDeprecate: "Erro ao marcar como obsoleta",
    errUndeprecate: "Erro ao reativar versão",
    deletePromptTitle: "Apagar versão {version} ({sha})?",
    deletePromptBody:
      "Esta operação:\n" +
      "  - Remove o registro do banco\n" +
      "  - Remove o .bin do filesystem\n\n" +
      "Dispositivos que JÁ baixaram este firmware não são afetados.\n" +
      "Dispositivos com OTA pendente vão falhar até disparar novo.\n\n" +
      'Dica: para apenas esconder + bloquear implantações sem apagar,\n' +
      'use "Obsoletar" no lugar.',
    deprecatePromptTitle: "Marcar versão {version} como obsoleta?",
    deprecatePromptBody:
      "Efeito:\n" +
      '  - Esconde do NOC por padrão (filtro "Mostrar obsoletos" exibe)\n' +
      "  - Bloqueia novas implantações/OTAs individuais\n" +
      "  - NÃO apaga o arquivo físico\n" +
      '  - Reversível via "Reativar"\n\n' +
      "Motivo (opcional):",
    undeprecatePromptTitle: "Reativar versão {version}?",
    undeprecatePromptBody:
      "Após reativar, ela volta a aceitar implantações e OTAs individuais.",
    rolloutPromptVersion:
      "Implantação — aplicar {version} em dispositivos com firmware igual a:\n\n" +
      "(Deixe vazio para dispositivos SEM firmware conhecido.)\n" +
      "Ex: 2.4.0",
    rolloutPromptLimit:
      "Limite (máximo de dispositivos a atualizar nesta chamada):\n\n" +
      "Backend impõe MAX=100 por chamada. Faça várias implantações para frota maior.",
    rolloutLimitInvalid: "Limite inválido",
    rolloutConfirm:
      "Confirma implantação?\n\n" +
      "Versão alvo:        {version}\n" +
      "Filtro firmware:    {filter}\n" +
      "Limite:             {limit}\n\n" +
      "OTAs serão enfileirados em até {limit} dispositivos que:\n" +
      "  - estão na versão filtrada\n" +
      "  - estão ONLINE\n" +
      "  - têm canal seguro (secure_channel=true)\n" +
      "  - NÃO têm OTA já pendente",
    rolloutWithoutFwLabel: "(SEM firmware conhecido)",
    rolloutDoneTitle: "Implantação enviada.",
    rolloutDoneBody:
      "Enfileirados: {enq}\n" +
      "Pulados:      {skp}\n",
    rolloutDoneReasonsPrefix: "Razões pulados: ",
  },

  // -------------------------------------------------------------------
  // Diff (preview de alterações) — manter siglas técnicas
  // -------------------------------------------------------------------
  diff: {
    title: "Comparação",
    titleHeader: "Pré-visualização de Alterações",
    new: "NOVO",
    changed: "ALTERADO",
    risky: "RISCO",
    changesCount: "{n} mudança(s)",
    ssidChanges: "SSID muda",                // SSID preservado
    passwordChanges: "Senha Wi-Fi muda",     // Wi-Fi preservado
    dhcpToStatic: "DHCP → IP fixo",          // DHCP, IP preservados
    staticToDhcp: "IP fixo → DHCP",
    ipChanges: "IP fixo muda",
    gatewayChanges: "Gateway muda",          // Gateway preservado
    hostnameChanges: "Hostname muda",        // Hostname preservado
    scheduleChanges: "Agenda muda",
    adminPasswordChanges: "Senha admin muda",
    nothingToChange: "nada a mudar",
    passwordWarning: "Senha não é exposta no snapshot — sempre tratada como mudança sensível.",
  },

  // -------------------------------------------------------------------
  // Wi-Fi config — Wi-Fi e SSID preservados
  // -------------------------------------------------------------------
  wifi: {
    title: "Rede Wi-Fi",
    ssid: "SSID",                          // preservado
    password: "Senha",
    passwordPlaceholder: "Deixe em branco para manter atual",
    dhcp: "DHCP",                          // preservado
    staticIp: "IP fixo",
    ip: "IP",                              // preservado
    mask: "Máscara",
    gateway: "Gateway",                    // preservado
    dns1: "DNS 1",                          // preservado
    dns2: "DNS 2",
    showPassword: "Mostrar senha",
  },

  // -------------------------------------------------------------------
  // Watchdog (Supervisão)
  // -------------------------------------------------------------------
  watchdog: {
    title: "Supervisão (Watchdog)",        // watchdog preservado entre parênteses
    enabled: "Supervisão habilitada",
    host: "Host",
    host1: "Host 1",
    host2: "Host 2",
    mode: "Modo",
    interval: "Intervalo",
    failureThreshold: "Limiar de falhas",
    rebootDuration: "Duração do reboot",
    action: "Ação",
    actionRelayReboot: "Reiniciar relé",   // relay_reboot fica em texto
    actionEspReboot: "Reiniciar ESP32",
    actionAlertOnly: "Apenas alertar",
    pingTarget: "Alvo de ping",
  },

  // -------------------------------------------------------------------
  // Sprint 5.0 — Provisionamento QR + IXC
  // -------------------------------------------------------------------
  provisioning: {
    title: "Provisionamento",
    subtitle:
      "Vincule um QR físico Sonoff a um cliente IXC: leia o serial, " +
      "busque o cliente, escolha contrato e login PPPoE, confirme.",
    btnTestIxc: "TESTAR IXC",

    // Stepper
    stepQr: "Ler QR",
    stepCustomer: "Buscar Cliente",
    stepContract: "Contrato",
    stepLogin: "Login PPPoE",
    stepConfirm: "Confirmar",

    // Step 1
    step1Title: "Passo 1 — Leitura do QR",
    step1Help:
      "Digite ou escaneie o serial do QR físico do Sonoff. O device_id " +
      "é opcional — pode ser preenchido depois quando o dispositivo enviar " +
      "o primeiro heartbeat.",
    qrSerial: "Serial do QR",
    scanQrBtn: "Ler QR",
    scanQrTooltip: "Ler o QR do Sonoff pela câmera",
    deviceIdOptional: "device_id (opcional)",
    deviceIdLabel: "device_id",

    // Step 2
    step2Title: "Passo 2 — Buscar Cliente no IXC",
    step2Help:
      "Pesquise por id IXC, nome (razão social) ou CPF/CNPJ. " +
      "A busca é online — feita direto na API IXC.",
    searchPlaceholder: "id, nome do cliente, CPF/CNPJ…",
    searchBtn: "Buscar",
    noResults: "Nenhum cliente encontrado para esta busca.",
    errSearch: "Erro ao buscar cliente no IXC.",
    errSearchPrefix: "Falha ao buscar cliente no IXC",

    // Sprint 5.0.3 — Resultado do bind enriquecido
    deviceFound: "Dispositivo encontrado",
    deviceWaiting:
      "Vínculo pendente — será associado automaticamente quando o " +
      "equipamento conectar.",
    locationUpdated: "Localização atualizada",
    locationMissing:
      "IXC sem coordenadas — localização não atualizada. " +
      "Cadastre lat/lng no cliente, contrato ou login PPPoE no IXC.",
    locationSource: {
      contract: "do contrato",
      radius: "do login PPPoE",
      customer: "do cliente",
    },
    otherWarnings: "Outros avisos",

    // Sprint 5.2A — Abas da listagem + aba Pendentes
    tabAll: "Todos os vínculos",
    tabPending: "Pendentes",
    pendingTab: {
      title: "Vínculos pendentes",
      autoHint:
        "Atualiza sozinho — o vínculo some daqui quando o device conecta.",
      empty: "Nenhum vínculo pendente no momento.",
      colQr: "QR",
      colCustomer: "Cliente",
      colTemplate: "Template",
      colCreated: "Criado",
      colStatus: "Status",
      colDevice: "Dispositivo",
      badgePending: "PENDENTE",
      badgeBound: "VINCULADO",
      countSuffix: "{n} vínculo(s) pendente(s)",
    },

    // Sprint 5.0.6 — Template inicial
    templateSectionTitle: "Template inicial",
    templateSectionHelp:
      "Configuração aplicada automaticamente após o vínculo.",
    templatePreviewBtn: "Ver comandos do template",
    templatePreviewTitle: "Preview do template",
    templateNotFound: "Template não encontrado.",
    templateCommandsLabel: "Comandos que serão enfileirados",
    templateNoCommands:
      "Este template não aplica nenhuma configuração automática.",
    templateApplied: "Template aplicado:",
    templateCommandsEnqueued: "{n} comando(s) enfileirado(s)",
    templateSkipped:
      "Template personalizado — nenhuma configuração automática aplicada.",
    templateSkippedNoDevice:
      "Template não aplicado — device ainda não existe. " +
      "Reaplique quando o device aparecer.",
    errSummary:
      "Erro ao carregar dados do cliente (contratos/logins). " +
      "Verifique a conexão IXC.",

    // Step 3
    step3Title: "Passo 3 — Selecionar Contrato",
    noContracts: "Cliente sem contratos cadastrados no IXC.",
    noContractName: "(sem nome)",

    // Step 4
    step4Title: "Passo 4 — Selecionar Login PPPoE",
    noLogins: "Cliente sem logins RADIUS/PPPoE cadastrados no IXC.",
    noLoginName: "(sem login)",

    // Step 5
    step5Title: "Passo 5 — Confirmar Vínculo",
    customerLabel: "Cliente",
    contractLabel: "Contrato",
    loginLabel: "Login PPPoE",
    onlineLabel: "Status",
    confirmBind: "Confirmar vínculo",
    binding: "Vinculando…",
    bindSuccess: "Vínculo criado com sucesso. ID:",
    errBind:
      "Erro ao criar vínculo. Verifique os campos e tente novamente.",
    newBinding: "Novo vínculo",

    // Navegação
    next: "Próximo",
    back: "Voltar",

    // Modal TESTAR IXC
    testIxcTitle: "Teste de conexão IXC",
    testing: "Testando…",
    testStatus: "Status",
    testLatency: "Latência",
    testAuthStatus: "Status auth",
    testServerInfo: "Server info",
    testRawOutput: "Resultado bruto",
    testRetry: "Tentar novamente",

    // Hotfix S5.0.1 — Tabela de vínculos existentes
    bindingsTable: {
      title: "Vínculos existentes",
      subtitle: "Todos os QRs já provisionados, ordenados pelo mais recente.",
      searchPlaceholder: "Buscar: QR, device, cliente, login PPPoE…",
      refresh: "Recarregar",
      empty:
        "Nenhum vínculo cadastrado ainda. Complete o wizard acima " +
        "para criar o primeiro.",
      noMatch: "Nenhum vínculo bate com a busca.",
      colQr: "QR",
      colDevice: "Dispositivo",
      colCustomer: "Cliente",
      colContract: "Contrato",
      colLogin: "Login PPPoE",
      colStatus: "Status",
      colTemplate: "Template",
      colCreatedAt: "Criado em",
      countSuffix: "{n} vínculo(s) listado(s)",
      errLoad: "Erro ao carregar lista de vínculos.",
      // Sprint 5.0.3
      statusBound: "vinculado",
      statusPending: "aguardando",
    },
  },

  // -------------------------------------------------------------------
  // Sprint 5.2B — Portal do Técnico (/installer)
  // -------------------------------------------------------------------
  installer: {
    appTitle: "IP WatchDog — Instalação",
    appSub: "Assistente de instalação em campo",
    creating: "Preparando a instalação…",
    // Barra de progresso (Hotfix 5.2B-C — sem WiFi)
    progQr: "QR",
    progCustomer: "Cliente",
    progTemplate: "Template",
    progFinish: "Finalizar",
    // Hotfix 5.2B-C — tela inicial (WiFi é feito no portal do Sonoff)
    welcomeTitle: "Antes de começar",
    welcomeWifiHint:
      "Configure o Wi-Fi do equipamento no portal local do Sonoff " +
      "e depois prossiga.",
    welcomeStep1:
      "Conecte-se à rede Wi-Fi do equipamento (IPWD-XXXX).",
    welcomeStep2:
      "Abra http://192.168.4.1 e configure a rede do cliente.",
    welcomeStep3:
      "Volte aqui e toque em Iniciar para registrar a instalação.",
    welcomeStart: "Iniciar instalação",
    // Hotfix 5.2B-C — status do equipamento na tela final
    deviceStatusTitle: "Status do equipamento",
    deviceStatusLabel: "Conexão",
    deviceWaiting: "Aguardando o equipamento conectar…",
    deviceOnline: "Online",
    deviceOffline: "Offline",
    deviceFw: "Firmware",
    deviceLastHb: "Último heartbeat",
    // Passo 1 — Equipamento
    step2Title: "Identificar equipamento",
    qrSerial: "Serial do QR",
    scanQr: "Ler QR pela câmera",
    qrInvalid: "Serial inválido (8 a 64 caracteres).",
    // Passo 3 — Cliente
    step3Title: "Cliente IXC",
    searchLabel: "Buscar cliente",
    searchPlaceholder: "Nome, CPF ou ID",
    noResults: "Nenhum cliente encontrado.",
    contract: "Contrato",
    pppoeLogin: "Login PPPoE",
    selectOne: "Selecione…",
    changeCustomer: "Trocar cliente",
    // Passo 4 — Template
    step4Title: "Escolher template",
    template: "Template",
    // Passo 5 — Finalizar
    step5Title: "Revisar e instalar",
    customer: "Cliente",
    install: "Instalar",
    installing: "Instalando…",
    // Hotfix 5.2B-B — persistência e validação da sessão
    saveFailed: "Falha ao salvar este passo",
    sessionReady: "Sessão pronta para instalar",
    sessionIncomplete: "Campos pendentes",
    doneTitle: "Instalação registrada!",
    doneHelp:
      "O vínculo foi criado. Quando o equipamento conectar à internet, " +
      "ele será associado automaticamente e configurado.",
    newInstall: "Nova instalação",
    // Navegação
    next: "Próximo",
    cancel: "Cancelar instalação",
  },

  // -------------------------------------------------------------------
  // Sprint 5.2B — Aba Instalações no NOC
  // -------------------------------------------------------------------
  installations: {
    title: "Instalações em campo",
    autoHint: "Atualiza sozinho a cada 15s.",
    empty: "Nenhuma instalação registrada ainda.",
    colCustomer: "Cliente",
    colQr: "QR",
    colTemplate: "Template",
    colInstaller: "Instalador",
    colStatus: "Status",
    colDevice: "Dispositivo",
    countSuffix: "{n} instalação(ões)",
    statusDraft: "RASCUNHO",
    statusWifiDone: "AGUARDANDO WIFI",
    statusPendingDevice: "AGUARDANDO HB",
    statusBound: "VINCULADO",
    statusFailed: "CANCELADA",
  },

  // -------------------------------------------------------------------
  // Sprint 5.2D — Estoque + Reserva + Logística
  // -------------------------------------------------------------------
  inventory: {
    title: "Estoque de equipamentos",
    empty: "Nenhum item de estoque cadastrado.",
    total: "Total",
    offlineTag: "offline",
    // Filtros
    filterAll: "Todos",
    filterOffline: "Offline",
    // Status (STOCK-SONOFF-INVENTORY-5 — máquina de 8 status)
    statusStock: "Em estoque",
    statusComTecnico: "Com técnico",
    statusReserved: "Reservados",
    statusInstalled: "Instalados",
    statusDevolvido: "Devolvidos",
    statusDefeito: "Defeito",
    statusRma: "RMA",
    statusMaintenance: "Manutenção",
    statusLost: "Perdidos",
    status_stock: "ESTOQUE",
    status_com_tecnico: "COM TÉCNICO",
    status_reserved: "RESERVADO",
    status_installed: "INSTALADO",
    status_devolvido: "DEVOLVIDO",
    status_defeito: "DEFEITO",
    status_rma: "RMA",
    status_maintenance: "MANUTENÇÃO",
    status_lost: "PERDIDO",
    // Colunas
    colStatus: "Status",
    colCustomer: "Cliente",
    colInstaller: "Instalador",
    colTech: "Técnico",
    colModel: "Modelo / Lote",
    colTemplate: "Template",
    colActions: "Ações",
    // Botões
    btnNew: "Novo item estoque",
    btnBulk: "Cadastro em massa",
    btnReserve: "Reservar instalação",
    btnEdit: "Editar",
    btnAssign: "Distribuir",
    btnTransfer: "Transferir",
    btnReturn: "Devolver",
    btnDefect: "Defeito",
    btnRma: "RMA",
    btnReadmit: "Readmitir",
    btnHistory: "Histórico",
    btnMaintenance: "Manutenção",
    btnLost: "Perdido",
    // Ações com motivo (STOCK-5)
    reasonLabel: "Motivo",
    reasonPlaceholder: "Motivo obrigatório da movimentação…",
    noFieldUsers: "Nenhum usuário com papel 'técnico de campo' (field) cadastrado.",
    // Movimentações
    movTitle: "Movimentações",
    movEmpty: "Nenhuma movimentação registrada para este item.",
    mov_inv_create: "CADASTRO",
    mov_inv_assign: "DISTRIBUIÇÃO",
    mov_inv_transfer: "TRANSFERÊNCIA",
    mov_inv_return: "DEVOLUÇÃO",
    mov_inv_defect: "DEFEITO",
    mov_inv_lost: "PERDIDO",
    mov_inv_rma: "RMA",
    mov_inv_reserve: "RESERVA",
    mov_inv_correct: "CORREÇÃO",
    mov_inv_install: "INSTALAÇÃO",
    mov_inv_owner_warn: "⚠ POSSE DIVERGENTE",
    mov_install: "INSTALAÇÃO",
    // Cadastro em massa
    bulkLines: "Um QR por linha (opcional: QR,MAC)",
    bulkCreated: "Criados",
    bulkSkipped: "Ignorados",
    // Campos do formulário
    field_qr_serial: "QR Serial",
    field_device_id: "ID do Dispositivo",
    field_mac: "MAC",
    field_fw: "Firmware",
    field_status: "Status",
    field_model: "Modelo (SKU)",
    field_batch: "Lote",
    field_customer_id: "Cliente (ID IXC)",
    field_contract_id: "Contrato (ID IXC)",
    field_installer: "Instalador",
    field_team: "Equipe",
    field_location: "Localização",
    field_notes: "Observações",
    field_scheduled_at: "Data prevista",
  },

  // -------------------------------------------------------------------
  // Sprint 5.3 — Saúde da Frota (Fleet Health)
  // -------------------------------------------------------------------
  health: {
    title: "Saúde da frota",
    autoHint: "Atualiza sozinho a cada 15s.",
    empty: "Nenhum device com dados de saúde ainda.",
    total: "Total",
    // Filtros
    filterAll: "Todos",
    filterOnline: "Online",
    filterOffline: "Offline",
    filterWarning: "Atenção",
    filterCritical: "Crítico",
    filterWeakSignal: "RSSI baixo",
    filterWatchdogHigh: "Watchdog alto",
    // Cards
    cardOnline: "Online",
    cardOffline: "Offline",
    cardWeakSignal: "Sinal ruim",
    cardWatchdog: "Watchdog",
    cardReboots: "Reboots altos",
    cardCritical: "Críticos",
    // Colunas
    colCustomer: "Cliente",
    colWatchdog: "Watchdog",
    colReboots: "Reboots",
    colStatus: "Status",
    colLastOnline: "Último online",
    // Status
    status_healthy: "SAUDÁVEL",
    status_warning: "ATENÇÃO",
    status_critical: "CRÍTICO",
    status_offline: "OFFLINE",
  },

  // -------------------------------------------------------------------
  // Sprint 5.5 — Equipe Técnica + Ordens de Serviço
  // -------------------------------------------------------------------
  teams: {
    title: "Equipes & Ordens de Serviço",
    // Cards
    cardTeams: "Equipes",
    cardWoOpen: "OS abertas",
    cardWoProgress: "OS em andamento",
    cardWoInstalled: "Instalações concluídas",
    // Tabelas
    tableTeams: "Equipes técnicas",
    tableWo: "Ordens de serviço",
    emptyTeams: "Nenhuma equipe cadastrada.",
    emptyWo: "Nenhuma ordem de serviço.",
    // Colunas — equipes
    colName: "Nome",
    colLeader: "Líder",
    colTechs: "Qtd técnicos",
    colInstalls: "Instalações",
    // Colunas — OS
    colCustomer: "Cliente",
    colTeam: "Equipe",
    colInstaller: "Instalador",
    colStatus: "Status",
    colDate: "Data prevista",
    // Botões
    btnNewTeam: "Nova equipe",
    btnNewWo: "Nova OS",
    btnEdit: "Editar",
    // Status da OS
    wo_status_open: "ABERTA",
    wo_status_scheduled: "AGENDADA",
    wo_status_in_progress: "EM ANDAMENTO",
    wo_status_installed: "INSTALADA",
    wo_status_cancelled: "CANCELADA",
    // Campos do formulário
    fieldTeam: "Equipe",
    field_name: "Nome",
    field_leader: "Líder",
    field_phone: "Telefone",
    field_notes: "Observações",
    field_customer_id: "Cliente (ID IXC)",
    field_contract_id: "Contrato (ID IXC)",
    field_qr_serial: "QR Serial",
    field_installer: "Instalador",
    field_scheduled_at: "Data prevista",
    field_location: "Localização",
  },

  // -------------------------------------------------------------------
  // Sprint 5.6 — Templates Automáticos por Perfil
  // -------------------------------------------------------------------
  rules: {
    title: "Regras de Template",
    empty: "Nenhuma regra cadastrada.",
    total: "Total",
    confirmDelete: "Excluir esta regra de template?",
    // Cards
    cardEnabled: "Regras ativas",
    cardDisabled: "Regras inativas",
    cardProfiles: "Perfis",
    cardTemplatesLinked: "Templates vinculados",
    // Perfis
    profile_residential: "RESIDENCIAL",
    profile_business: "EMPRESARIAL",
    profile_vip: "VIP",
    profile_critical: "CRÍTICO",
    // Colunas
    colName: "Nome",
    colProfile: "Perfil",
    colTemplate: "Template",
    colPriority: "Prioridade",
    colEnabled: "Ativa",
    colCity: "Cidade",
    colPlan: "Plano",
    colCustomerType: "Tipo cliente",
    colContractType: "Tipo contrato",
    colActions: "Ações",
    // Botões
    btnNew: "Nova regra",
    btnEdit: "Editar",
    btnEnable: "Ativar",
    btnDisable: "Desativar",
    btnDelete: "Excluir",
    // Campos do formulário
    field_name: "Nome",
    field_profile: "Perfil",
    field_priority: "Prioridade",
    field_description: "Descrição",
    field_match_city: "Cidade (critério)",
    field_match_plan_name: "Plano (critério)",
    field_match_customer_type: "Tipo de cliente (critério)",
    field_match_contract_type: "Tipo de contrato (critério)",
    // Sugestão (Installer / OS)
    suggestedTemplate: "Template sugerido automaticamente",
    suggestReason: "Motivo da sugestão",
    btnChangeManual: "Alterar manualmente",
  },

  // -------------------------------------------------------------------
  // Sprint 5.7 — Logs + Auditoria + Timeline
  // -------------------------------------------------------------------
  audit: {
    title: "Auditoria & Logs",
    empty: "Nenhum evento registrado.",
    showing: "Exibindo",
    exportCsv: "Exportar CSV",
    exportJson: "Exportar JSON",
    // Cards
    cardEventsToday: "Eventos hoje",
    cardInstalls: "Instalações",
    cardHealthChanges: "Mudanças saúde",
    cardTemplates: "Templates aplicados",
    cardAutoBind: "Auto-bind",
    // Colunas
    colDate: "Data",
    colEntity: "Entidade",
    colAction: "Ação",
    colActor: "Usuário",
    colSource: "Origem",
    colDetails: "Detalhes",
    // Filtros
    filterEntity: "Entidade",
    filterAction: "Ação",
    filterActor: "Usuário",
    filterDevice: "Dispositivo",
    // entity_type
    entity_device: "Dispositivo",
    entity_inventory: "Estoque",
    entity_health: "Saúde",
    entity_work_order: "Ordem de Serviço",
    entity_team: "Equipe",
    entity_template_rule: "Regra de Template",
    entity_installer: "Instalação",
    entity_admin: "Admin",
    // action
    action_create: "criar",
    action_update: "atualizar",
    action_delete: "excluir",
    action_reserve: "reservar",
    action_install: "instalar",
    action_bind: "vincular",
    action_health_change: "mudança de saúde",
    action_status_change: "mudança de status",
    action_template_apply: "aplicar template",
    // Timeline
    timelineTitle: "Linha do tempo",
    timelineEmpty: "Sem eventos para este device.",
  },

  // -------------------------------------------------------------------
  // Sprint 5.8 — Observabilidade
  // -------------------------------------------------------------------
  observability: {
    title: "Observabilidade",
    autoHint: "Atualiza sozinho a cada 15s.",
    grafanaHint:
      "O Grafana (dashboard IP WatchDog Field Ops) traz a visão "
      + "histórica. O Prometheus coleta de",
    // Cards
    cardHbRate: "Heartbeats",
    cardOnline: "Online",
    cardOffline: "Offline",
    cardDevicesTotal: "Total de dispositivos",
    cardInventory: "Estoque",
    cardHealthWarning: "Saúde atenção",
    cardHealthCritical: "Saúde crítica",
    cardWorkOrders: "OS abertas",
    cardAutoBind: "Auto-bind (total)",
    cardTemplates: "Templates aplicados",
    cardAudit: "Eventos de auditoria",
  },

  // -------------------------------------------------------------------
  // Sprint 5.9 — Alertas + Notificações
  // -------------------------------------------------------------------
  alerts: {
    title: "Alertas & Notificações",
    empty: "Nenhum alerta registrado.",
    showing: "Exibindo",
    scanDone: "Varredura concluída",
    testOk: "Alerta de teste entregue",
    // Cards
    cardOpen: "Abertos",
    cardCritical: "Críticos",
    cardToday: "Hoje",
    cardAck: "Reconhecidos",
    // Colunas
    colDate: "Data",
    colSeverity: "Severidade",
    colSource: "Origem",
    colMessage: "Mensagem",
    colChannel: "Canal",
    colStatus: "Status",
    colActions: "Ações",
    // Filtros
    filterSeverity: "Severidade",
    filterStatus: "Status",
    filterEntity: "Entidade",
    // Botões
    btnAck: "Reconhecer",
    btnClose: "Fechar",
    btnTest: "Testar canal",
    btnScan: "Rodar varredura",
    // Severidade
    sev_info: "INFO",
    sev_warning: "ATENÇÃO",
    sev_critical: "CRÍTICO",
    // Status
    status_open: "ABERTO",
    status_ack: "RECONHECIDO",
    status_closed: "FECHADO",
    // entity_type
    entity_device: "Dispositivo",
    entity_health: "Saúde",
    entity_work_order: "Ordem de Serviço",
    entity_inventory: "Estoque",
    entity_admin: "Admin",
    // Canais
    channelReady: "Canal habilitado e configurado.",
    channelNotConfigured: "Canal habilitado mas sem credenciais (dry-run).",
    channelDisabled: "Canal desabilitado.",
  },

  // -------------------------------------------------------------------
  // Sprint 6.0 — Automação / Playbooks IF-THEN
  // -------------------------------------------------------------------
  automation: {
    title: "Automação & Playbooks",
    empty: "Nenhuma regra de automação cadastrada.",
    runsEmpty: "Nenhuma execução registrada.",
    rulesTitle: "Regras IF/THEN",
    runsTitle: "Execuções recentes",
    scanDone: "Varredura concluída",
    runDone: "Execução concluída",
    // Cards
    cardEnabled: "Regras ativas",
    cardRunsToday: "Execuções hoje",
    cardDryRun: "Em dry-run",
    cardFailures: "Falhas hoje",
    // Colunas — regras
    colName: "Nome",
    colTrigger: "Gatilho",
    colAction: "Ação",
    colCooldown: "Cooldown",
    colDryRun: "Dry-run",
    colStatus: "Status",
    colActions: "Ações",
    // Colunas — execuções
    colDate: "Data",
    colEntity: "Entidade",
    colResult: "Resultado",
    // Botões
    btnNew: "Nova regra",
    btnEdit: "Editar",
    btnEnable: "Ativar",
    btnDisable: "Desativar",
    btnRun: "Executar",
    btnScan: "Rodar varredura",
    // Gatilhos
    trigger_device_offline: "Dispositivo offline",
    trigger_health_critical: "Saúde crítica",
    trigger_work_order_late: "OS atrasada",
    // Ações
    action_raise_alert: "Criar alerta",
    action_notify: "Notificar",
    action_relay_reboot: "Reiniciar (relay_reboot)",
    // Status de execução
    status_pending: "PENDENTE",
    status_success: "SUCESSO",
    status_failed: "FALHA",
    status_skipped: "IGNORADO",
    status_dry_run: "DRY-RUN",
    // Campos do formulário
    field_name: "Nome da regra",
    field_trigger: "Gatilho (IF)",
    field_action: "Ação (THEN)",
    field_cooldown: "Cooldown (s)",
    field_maxRuns: "Máx. execuções/dia",
    field_windowStart: "Janela início (HH:MM)",
    field_windowEnd: "Janela fim (HH:MM)",
    field_dryRun: "Dry-run (simular sem executar)",
  },
  // Sprint 5.0.4 — Scanner de QR pela câmera
  // -------------------------------------------------------------------
  qrScanner: {
    title: "Ler QR pela câmera",
    starting: "Abrindo câmera…",
    searching: "Procurando QR…",
    detectedLabel: "QR detectado:",
    invalidQr:
      "Este QR não parece válido (esperado: 8 a 64 caracteres, " +
      "apenas letras, números, hífen e underscore).",
    manualEntry: "Inserir manualmente",
    stopCamera: "Parar câmera",
    rescan: "Ler de novo",
    useThis: "Usar este",
    retry: "Tentar novamente",
    errPermissionDenied:
      "Permissão de câmera negada. Autorize o acesso à câmera nas " +
      "configurações do navegador e tente novamente.",
    errNoCamera: "Nenhuma câmera encontrada neste dispositivo.",
    errNoGetUserMedia:
      "Este navegador não suporta acesso à câmera, ou a página não " +
      "está em HTTPS. Use a entrada manual.",
    errGeneric:
      "Não foi possível abrir a câmera. Tente novamente ou use a " +
      "entrada manual.",
  },

  // -------------------------------------------------------------------
  // Sprint 5.0.3 — Card "Vínculo IXC" no DeviceDetail e popup do mapa
  // -------------------------------------------------------------------
  ixcBinding: {
    title: "Vínculo IXC",
    statusBound: "VINCULADO",
    empty: "Sem vínculo IXC para este device.",
    emptyHint:
      "Use a página Provisionamento para vincular este device " +
      "a um cliente IXC.",
    qrSerial: "QR Serial",
    customer: "Cliente",
    contract: "Contrato",
    pppoeLogin: "Login PPPoE",
    online: "Status",
    onlineYes: "online",
    onlineNo: "offline",
    template: "Template",
    snapshotNote:
      "Snapshot do momento do provisionamento. " +
      "Re-provisione via wizard para atualizar.",
  },

  // -------------------------------------------------------------------
  // Sprint 4.7.4 — Presets prontos de agenda
  // -------------------------------------------------------------------
  schedulePresets: {
    sectionTitle: "Presets rápidos",
    sectionHint: "5 modelos · clique para colapsar",
    intro:
      "Modelos prontos para os casos mais comuns. Preencha o editor " +
      "e ajuste antes de Aplicar.",
    btnPreview: "Pré-visualizar",
    btnApply: "Aplicar",
    btnApplyPreset: "Aplicar este preset",
    applyThisLabel: "Aplicar '{name}'?",
    applyHint:
      "Preset preenche o editor — você ainda precisa clicar Aplicar " +
      "no rodapé da aba para enviar ao dispositivo.",
    // Catálogo de presets
    hotspot: {
      name: "Hotspot comercial",
      desc: "Reinício diário para Wi-Fi de loja, restaurante, escritório.",
    },
    residential: {
      name: "Residencial",
      desc: "Reinício de madrugada todos os dias — uso doméstico.",
    },
    server: {
      name: "Servidor 24/7",
      desc: "Reinício semanal aos domingos para infraestrutura crítica.",
    },
    popRadio: {
      name: "POP / Rádio",
      desc: "Reinício diário leve para PoPs e links de rádio.",
    },
    maintenance: {
      name: "Janela de manutenção",
      desc:
        "Desliga o relé das 02:00 às 02:10 nas segundas, quartas e " +
        "sextas — útil para coordenar com upstream.",
    },
    // Toast após Aplicar preset
    appliedToast: "Preset '{name}' aplicado ao editor — clique Aplicar para enviar.",
  },

  // -------------------------------------------------------------------
  // Sprint 4.7.3 — Modal de busca para copiar agenda de outro device
  // -------------------------------------------------------------------
  scheduleSource: {
    btnOpen: "📋 Copiar de outro device",
    btnOpenDisabled: "Aguarde carregar a frota",
    title: "Copiar agenda de outro dispositivo",
    subtitle:
      "Selecione um dispositivo cuja agenda servirá de modelo. " +
      "O editor será preenchido — você ainda precisa clicar Aplicar.",
    searchPlaceholder: "Buscar: device_id, IP, firmware, cliente…",
    pickFromList: "Selecione um dispositivo na lista à esquerda.",
    selfNotAllowed:
      "Este é o próprio dispositivo que você está configurando — " +
      "escolha outro.",
    noFwVersion: "firmware desconhecido",
    noSnapshot:
      "Este dispositivo ainda não enviou heartbeat — não há agenda para copiar.",
    summaryTitle: "Agenda deste dispositivo",
    enabledField: "Habilitada",
    enabledYes: "sim",
    enabledNo: "não",
    entriesField: "Entradas",
    exceptionsField: "Exceções",
    lastEventField: "Última execução",
    emptyWarning:
      "Esta agenda está vazia. Você pode copiar mesmo assim — isso " +
      "vai desabilitar a agenda do dispositivo destino.",
    useThisSchedule: "Usar esta agenda",
    useThisHelp:
      "Preenche o editor local. O dispositivo destino só receberá " +
      "a alteração quando você clicar Aplicar.",
    neverSeen: "nunca visto",
    errLoadList: "Erro ao buscar dispositivos",
    errLoadSnapshot: "Erro ao carregar agenda do dispositivo de origem",
    copiedToast: "Agenda copiada para o editor — clique Aplicar para enviar.",
  },

  // -------------------------------------------------------------------
  // Sprint 4.7.2 — Pré-visualização de mudanças na Agenda (drawer)
  // -------------------------------------------------------------------
  schedulePreview: {
    title: "Pré-visualizar Alterações na Agenda",
    btnPreview: "🔬 Pré-visualizar Agenda",
    btnPreviewDisabled: "Carregue a agenda atual para pré-visualizar",
    identical: "Agenda proposta é idêntica à atual — nada a alterar.",
    colCurrent: "Atual",
    colProposed: "Proposta",
    entries: "entradas",
    changed: "alteradas",
    unchanged: "iguais",
    enabledTurnedOn: "Agenda passa a ficar HABILITADA",
    enabledTurnedOff: "Agenda passa a ficar DESABILITADA",
    exceptionsChange: "{added} exceção(ões) adicionada(s), {removed} removida(s)",
    exceptionsSectionTitle: "Exceções (datas a ignorar)",
    exceptionsAdded: "adicionadas",
    exceptionsRemoved: "removidas",
    bothEmpty: "Nenhuma entrada de agenda em nenhum dos lados.",
    noScheduleHere: "— sem agenda aqui —",
    removedSlot: "— removido —",
    disabledBadge: "desabilitada",
    status: {
      added: "novo",
      changed: "alterado",
      removed: "removido",
      unchanged: "igual",
    },
    // Operações ao lado do resumo
    copyCurrent: "Copiar atual",
    copyCurrentHelp:
      "Substitui o editor pela agenda atual lida do dispositivo",
    restoreCurrent: "Restaurar atual",
    restoreCurrentHelp:
      "Descarta edições e volta ao estado lido do dispositivo",
    clearAll: "Limpar tudo",
    clearAllHelp: "Remove todas as entradas e exceções do editor",
    confirmClearAll:
      "Limpar todas as entradas e exceções do editor?\n\n" +
      "Não envia nada ao dispositivo — só apaga o que você está editando.\n" +
      "Você pode \"Restaurar atual\" depois se quiser voltar.",
  },

  // -------------------------------------------------------------------
  // Agenda (Schedule)
  // -------------------------------------------------------------------
  schedule: {
    title: "Agenda",
    enabled: "Agenda habilitada",
    scopeNote:
      "Define as janelas de funcionamento do relé e/ou reinícios programados. " +
      "Substitui agenda anterior por completo.",
    errApply: "Erro ao aplicar agenda",
    history: "Histórico de execuções",
    historyEventCount: "{n} evento(s)",
    when: "Quando",
    entry: "Entrada",
    time: "Hora",
    action: "Ação",
    result: "Resultado",
    duration: "Duração",
    days: "Dias",
    onTime: "Hora ON",
    offTime: "Hora OFF",
    addEntry: "Adicionar entrada",
    removeEntry: "Remover entrada",
    exceptions: "Exceções",
    addException: "Adicionar exceção",
    noExceptions: "Nenhuma exceção",
    noEvents: "Nenhum evento registrado.",
    noEventsHint:
      "(firmware 2.7.3+ envia eventos no heartbeat após cada execução da agenda)",
    showingEvents: "Mostrando {n} de {total} eventos (mais recentes primeiro).",
    runStatus: {
      ok: "OK",
      fail: "FALHA",
      busy: "OCUPADO",
      skipped: "PULADO",
    },
  },

  // -------------------------------------------------------------------
  // OTA — OTA preservado
  // -------------------------------------------------------------------
  ota: {
    title: "OTA",
    triggerOta: "Disparar OTA",
    releases: "Versões disponíveis",
    triggerRollout: "Implantação gradual",
    gradualWizard: "Assistente de implantação",
    waveLabel: "Onda",
    coverage: "Cobertura",
    rollback: "Reverter",
    pause: "Pausar",
    resume: "Retomar",
    finish: "Finalizar",
    progress: "Progresso",
  },

  // -------------------------------------------------------------------
  // Avançado
  // -------------------------------------------------------------------
  advanced: {
    title: "Configuração Avançada",
    heartbeatInterval: "Intervalo de Heartbeat",  // Heartbeat preservado
    rssiThreshold: "Limiar RSSI",
    relayBoot: "Estado do relé no boot",
    relayBootRestore: "Restaurar último",
    relayBootOn: "Sempre ON",
    relayBootOff: "Sempre OFF",
    otaAuto: "OTA automático",         // OTA preservado
    safeMode: "Modo seguro",
    autoReboot: "Auto reboot (dias)",
    autoRebootDisabled: "0 = desabilitado",
  },

  // -------------------------------------------------------------------
  // Mensagens de erro
  // -------------------------------------------------------------------
  errors: {
    network: "Falha de rede",
    server: "Erro do servidor",
    unauthorized: "Sem permissão",
    notFound: "Não encontrado",
    invalid: "Inválido",
    required: "Obrigatório",
    tryAgain: "Tente novamente",
    tooManyDevices: "Muitos dispositivos selecionados",
    sessionExpired: "Sessão expirada",
    confirmRisky: "Confirme os riscos antes de aplicar",
    adminTokenMissing: "ADMIN_TOKEN não configurado",

    // ErrorBanner (Sprint UX-BR — refatorado nesta sprint)
    backendCommFailed: "Falha ao comunicar com o backend.",
    openSettings: "abrir configurações",
    hintNoConnection:
      "Verifique se o backend está rodando e se a URL está correta. " +
      "Em ambiente Docker, o frontend acessa o backend via http://<IP_SERVIDOR>:8000.",
    hintMissingToken:
      "Cabeçalho X-Admin-Token ausente. Defina o token em Configurações.",
    hintInvalidToken:
      "Token de admin inválido. Verifique o valor em Configurações.",
    hintNotFound:
      "Recurso não encontrado. Pode ser que o dispositivo foi removido.",
  },

  // -------------------------------------------------------------------
  // IXC (integração com sistema do provedor)
  // -------------------------------------------------------------------
  ixc: {
    title: "IXC — Integração",
    testConnection: "Testar conexão",
    syncCustomers: "Sincronizar clientes",
    syncInfra: "Sincronizar infraestrutura",
    syncFull: "Sincronização completa",
    tooltipNoCredentials: "Configure as credenciais primeiro",
    tooltipNotReady: "Configure credenciais e habilite primeiro",
    lastSync: "Último sync",
  },

  // -------------------------------------------------------------------
  // Mapa (Fleet Map)
  // -------------------------------------------------------------------
  map: {
    title: "Mapa da Frota",
    devicesShown: "{n} dispositivos no mapa",
    devicesHidden: "{n} sem localização cadastrada",
    cluster: "Agrupamento",
    layerSatellite: "Satélite",
    layerStreets: "Ruas",
    layerHybrid: "Híbrido",
    heatmap: "Mapa de calor",
    heatmapShow: "Mostrar mapa de calor",
    heatmapHide: "Esconder mapa de calor",
    filterRegion: "Filtrar por região",
    filterCustomer: "Filtrar por cliente",
    filterGroup: "Filtrar por grupo",
    filterTag: "Filtrar por tag",
    findDevice: "Encontrar dispositivo",
  },

  // -------------------------------------------------------------------
  // Tela de Configurações (Settings)
  // -------------------------------------------------------------------
  settingsPage: {
    apiUrl: "URL da API",
    adminToken: "Admin Token (X-Admin-Token)",
    adminTokenHelp: "Cole o token gerado em backend/.env",
    autoRefresh: "Auto-atualização (segundos)",
    autoRefreshHelp: "Intervalo entre atualizações automáticas. Mínimo 3s, máximo 300s.",
    save: "salvar",
    testConnection: "testar conexão",
    resetDefaults: "restaurar padrões",
    show: "mostrar",
    hide: "ocultar",
    testing: "testando…",
    deleteConfig: "Apagar configurações salvas?",
    backendOk: "Backend respondeu /health = { ok: true }.",
    persistedLocal: "Persistidas no localStorage do navegador. Não envia nada para servidor externo.",
    apiUrlHelp: "URL base do backend FastAPI. Inclui protocolo e porta.",
    backendNote: "Mesmo valor de ADMIN_TOKEN em backend/.env.",
  },

  // -------------------------------------------------------------------
  // Dashboard
  // -------------------------------------------------------------------
  dashboard: {
    title: "Painel",
    totalDevices: "Total de dispositivos",
    onlineDevices: "Dispositivos Online",
    offlineDevices: "Dispositivos Offline",
    pendingCommands: "Comandos pendentes",
    recentHeartbeats: "Heartbeats recentes",
    fleetVersions: "Versões da frota",
    otaActive: "OTAs ativas",
    latestErrors: "Erros recentes",
    onlineHistory: "Histórico online (últimas 24h)",
    recentCommands: "Últimos comandos",
    devicesOtaError: "Dispositivos com erro OTA (24h)",
    noDevicesWithOtaError: "Nenhum dispositivo com falha OTA nas últimas 24h ✓",
    rolloutsAudit: "Implantações — auditoria",
  },

  // S7.6-A — labels dos badges de aging e heap.
  // Usados como TOOLTIPS (texto visível continua sendo formatRelative
  // para aging e formatBytesKB para heap).
  offlineAging: {
    online: "online",
    warn: "Offline há mais de 10 minutos",
    alert: "Offline há mais de 1 hora",
    stale: "Offline há mais de 24 horas",
  },
  heapAlert: {
    ok: "Heap saudável (≥80 KB livres)",
    warn: "Heap baixo (50–80 KB livres)",
    crit: "Heap crítico (<50 KB livres)",
  },
};

export default ptBR;
